import { useCallback, useMemo } from 'react';
import Popover from '@mui/material/Popover';
import { anchorRef, bindPopover, bindTrigger, usePopupState } from 'material-ui-popup-state/hooks';

import { useMembershipComments } from '@occc-app/dataAccessLayer/membership/queries';
import { usePaginationParams } from '@occc-app/hooks/usePaginationParams';
import {
  COMMENTS_AFTER_PARAM,
  COMMENTS_BEFORE_PARAM,
  COMMENTS_PAGE_SIZE,
  COMMENTS_START_DATE_PARAM,
  COMMENTS_END_DATE_PARAM,
  COMMENTS_AUTHOR_EMAIL_PARAM_NAMES,
  COMMENTS_AUTHOR_EMAIL_PARAM,
  COMMENTS_PARAM_NAMES,
} from '@occc-app/routes/Member/MemberDetailsPage/constants';
import { DateTimeFormat, formatDateTime } from '@occc-app/lib/date';
import { useDateQueryParam } from '@occc-app/routes/Transactions/TransactionsListPage/hooks';
import { useSearchParams } from '@occc-app/hooks/useSearchParams';
import {
  TableContainer,
  TableHeader,
  Box,
  SearchForm,
  FilterButton,
  SearchFormProps,
  isFiltersSelected,
  Button,
} from '@occc-app/design-system';
import { useIsUserPermittedTo, Feature } from '@occc/utils-access-control';

import { Table, Row } from '@occc/ui-design-system';
import { CommentsFilterFormProps, CommentsFilterFormValues } from './comments-filter-form.type';
import { CommentsFilterForm } from './comments-filter-form.component';
import { makeFields } from './comments-filter-form.utils';

const headers = [{ label: 'Date' }, { label: 'Notes' }, { label: 'Comment by' }];

type CommentsProps = {
  membershipId: string;
  customerUuid: string | null;
  onCreateCommentClick(): void;
};

export const Comments = (props: CommentsProps) => {
  const { membershipId, customerUuid, onCreateCommentClick } = props;

  const popupState = usePopupState({ variant: 'popover', popupId: 'filters' });
  const [commentsStartDate] = useDateQueryParam(COMMENTS_START_DATE_PARAM);
  const [commentsEndDate] = useDateQueryParam(COMMENTS_END_DATE_PARAM);
  const [commentsAuthorEmail] = useDateQueryParam(COMMENTS_AUTHOR_EMAIL_PARAM);
  const showBadge = isFiltersSelected([commentsStartDate, commentsEndDate, commentsAuthorEmail]);
  const [search, setSearch] = useSearchParams(COMMENTS_PARAM_NAMES);
  const showAddBtn = useIsUserPermittedTo(Feature.MEMBER_EDIT) && Boolean(customerUuid);

  const [paginationInfo, onPageChange] = usePaginationParams({
    pageSize: COMMENTS_PAGE_SIZE,
    beforeParamName: COMMENTS_BEFORE_PARAM,
    afterParamName: COMMENTS_AFTER_PARAM,
  });
  const [startDate, setStartDate] = useDateQueryParam(
    COMMENTS_START_DATE_PARAM,
    COMMENTS_BEFORE_PARAM,
    COMMENTS_AFTER_PARAM,
  );
  const [endDate, setEndDate] = useDateQueryParam(
    COMMENTS_END_DATE_PARAM,
    COMMENTS_BEFORE_PARAM,
    COMMENTS_AFTER_PARAM,
  );
  const [authorEmail, setAuthorEmail] = useSearchParams(COMMENTS_AUTHOR_EMAIL_PARAM_NAMES);

  const popoverProps = bindPopover({
    ...popupState,
    isOpen: popupState.isOpen,
  });

  const handleReset = useCallback(() => {
    setStartDate(undefined);
    setEndDate(undefined);
    setAuthorEmail(undefined);
    popoverProps.onClose();
  }, [popoverProps, setAuthorEmail, setEndDate, setStartDate]);

  const handleSubmit = useCallback<CommentsFilterFormProps['onSubmit']>(
    (values) => {
      setStartDate(values.startDate);
      setEndDate(values.endDate);
      setAuthorEmail(values.author?.email);
      popoverProps.onClose();
    },
    [popoverProps, setAuthorEmail, setEndDate, setStartDate],
  );

  const initialValues: CommentsFilterFormValues = useMemo(() => {
    return {
      // We do not need author name for defining initial values
      author: authorEmail ? { email: authorEmail, name: undefined } : undefined,
      startDate,
      endDate,
    };
  }, [authorEmail, startDate, endDate]);

  const { comments, loading, error, paginationProps } = useMembershipComments({
    membershipId,
    paginationInfo,
    onPageChange,
    filter: {
      search,
      authorEmail,
      createdAtMin: startDate ? formatDateTime(startDate, DateTimeFormat.DateDashed) : undefined,
      createdAtMax: endDate ? formatDateTime(endDate, DateTimeFormat.DateDashed) : undefined,
    },
  });

  const rows = useMemo<Row[]>(
    () =>
      comments.map((comment) => ({
        key: comment.id,
        fields: makeFields(comment),
      })),
    [comments],
  );

  const onSearchSubmit: SearchFormProps['onSubmit'] = ({ search: value }) => {
    setSearch(value);
  };

  return (
    <TableContainer sx={{ boxShadow: 'none' }}>
      <TableHeader
        title='Comments'
        actions={
          <>
            <SearchForm onSubmit={onSearchSubmit} initialValues={{ search }} />
            <FilterButton
              {...bindTrigger(popupState)}
              ref={anchorRef(popupState)}
              showBadge={showBadge}
            />
            {showAddBtn && (
              <Button sx={{ ml: 1 }} onClick={onCreateCommentClick}>
                Add&nbsp;comment
              </Button>
            )}
          </>
        }
      />
      <Box>
        <Table
          headers={headers}
          rows={rows}
          loading={loading}
          error={error}
          paginationProps={paginationProps}
        />
        <Popover
          {...popoverProps}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          PaperProps={{
            sx: { maxWidth: 460 },
          }}
        >
          <CommentsFilterForm
            onSubmit={handleSubmit}
            onReset={handleReset}
            initialValues={initialValues}
          />
        </Popover>
      </Box>
    </TableContainer>
  );
};
